import { useMemo, useRef, useState } from 'react';
import { DateTimePicker } from '@mantine/dates';
import { TbCalendar, TbClipboardText } from 'react-icons/tb';
import { Box, Flex, Text } from '@mantine/core';
import { useRegisterCellRef } from '../useRegisterCellRef';
import { NewTableCellWrapper } from './style';
import ReportCellComment from '../../../../components/ReportComments/ReportCellComment';
import commonStore from '../../../../../../store/common';
import { HEADER_BG } from '../tableConstants';
import { idCreator } from '../../../../../Project/utils/idCreator';

const coerceToDate = value => {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
};

export const DateTimeCell = ({
  value,
  onChange,
  error = null,
  cellRefs = null,
  cellKey = null,
  onCellKeyDown = null,
  fundId = null,
  fundName = null,
  tableName = null,
  fieldName = null,
  fieldOrder = null,
  isFromStaticTemplate = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // Comment icon/popover mounts lazily on first hover (see SelectCellView).
  const [commentMounted, setCommentMounted] = useState(false);
  const cellRef = useRegisterCellRef({ cellRefs, cellKey });
  const inputRef = useRef(null);
  const { isViewer } = commonStore;
  const cellId = idCreator([fundId, tableName, fieldName, fieldOrder].filter(Boolean).join('-'));

  // Shift the comment icon left of any trailing icons so it doesn't overlap them.
  const commentRightOffset = useMemo(() => {
    let offset = 28;
    if (isFromStaticTemplate) offset += 28;
    return offset;
  }, [isFromStaticTemplate]);

  const handleKeyDown = e => {
    if (
      onCellKeyDown &&
      cellKey &&
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      if (!e.target.closest('.mantine-DateTimePicker-dropdown')) {
        onCellKeyDown(e, cellKey);
      }
    }
  };

  return (
    <NewTableCellWrapper
      ref={cellRef}
      sx={() => ({
        border: isFocused ? '1px solid #228BE6' : '1px solid transparent'
      })}
      pos="relative"
      $commentRightOffset={commentRightOffset}
      onPointerEnter={() => setCommentMounted(true)}
    >
      <Flex align="center" gap="4px" justify="space-between">
        <DateTimePicker
          id={`formpf-datetime-cell-${cellId}`}
          ref={inputRef}
          w="100%"
          styles={{
            input: {
              border: 'none',
              height: '100%',
              '&:disabled': {
                backgroundColor: HEADER_BG
              }
            }
          }}
          h="100%"
          value={coerceToDate(value)}
          onChange={onChange}
          rightSection={<TbCalendar size={16} />}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          popoverProps={{
            styles: {
              dropdown: {
                zIndex: '99999999!important'
              }
            }
          }}
          disabled={isViewer || isFromStaticTemplate}
        />
        {isFromStaticTemplate && (
          <TbClipboardText style={{ minWidth: '12px', marginRight: '12px' }} fontSize="12px" />
        )}
      </Flex>
      {error && (
        <Box p="7.5px 12px" h="100%" fz="12px" lh="17px">
          <Text id={`formpf-datetime-cell-error-${cellId}`} fz="12px" mt="4px" c="red">
            {error.msg}
          </Text>
        </Box>
      )}
      <ReportCellComment
        fundId={fundId}
        fundName={fundName}
        tableName={tableName}
        fieldName={fieldName}
        fieldOrder={fieldOrder}
        cellHovered={commentMounted}
      />
    </NewTableCellWrapper>
  );
};
